// Global packages or components
import { MetaFunction, useLoaderData } from '@remix-run/react'
import { getCarouselProducts, resolveBlocks } from '~/.server'
import { BlogSingle } from '~/components'
import { getBlogPost } from '~/sanity'
import { getMeta, getSchema, Schema } from '~/utils'
import { usePageInfo } from '~/hooks'

// Types
import type { LoaderFunction, LoaderFunctionArgs } from '@remix-run/node'
import type { SanityConceptItem } from '~/types'

// Route loader
export const loader: LoaderFunction = async ({
  request,
  params,
}: LoaderFunctionArgs) => {
  const env = process.env
  const url = new URL(request.url)
  const slug = params.slug

  if (!slug) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  // Sanity fetch
  const data = await getBlogPost({
    dataset: env?.ENVIRONMENT,
    projectId: env?.SANITY_PROJECT,
    slug,
  })

  // Page not found
  if (!data) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  // Server renders the content blocks' carousels and the related products
  const [resolved, relatedProducts] = await Promise.all([
    resolveBlocks({ blocks: data?.flexibleContent, request }),
    getCarouselProducts(data?.productCodes),
  ])

  // Return data
  return Response.json({
    seo: data?.seo,
    path: url?.pathname,
    title: data?.title,
    content: resolved.blocks,
    categories: data?.categories,
    slug: data?.slug,
    image: data?.image,
    bannerSidebar:
      data?.addSidebarBanner && data?.bannerSidebar ? data.bannerSidebar : null,
    fullWidthImage: data?.fullWidthImage,
    author: data?.author,
    createdAt: new Date(data._createdAt).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    productCodes: data?.productCodes,
    relatedProducts,
    relatedPosts: data?.relatedPosts,
  })
}

// Route meta
export const meta: MetaFunction = ({ matches, data }) => {
  return getMeta({
    matches,
    data,
  })
}

// Route view
export default function Index() {
  const {
    title,
    content,
    categories,
    bannerSidebar,
    fullWidthImage,
    author,
    createdAt,
    productCodes,
    relatedProducts,
    relatedPosts,
    seo,
    image,
    path,
  } = useLoaderData<typeof loader>()

  usePageInfo({
    pageType: 'Blog Post',
    pageTitle: title,
  })

  const blogPostSchemaInput = {
    title,
    description: seo?.metaDescription ?? '',
    slug: path,
    author: typeof author === 'string' ? author : author?.name ?? undefined,
    publishedAt: seo?.publishedAt ?? undefined,
    updatedAt: seo?.updatedAt ?? undefined,
    image: image?.asset?.url ?? image?.url ?? undefined,
  }

  const jsonLd = [
    getSchema({
      type: 'page',
      page: {
        ...seo,
        redirect: path,
      },
    }),
    getSchema({ type: 'blogPost', post: blogPostSchemaInput }),
  ]

  return (
    <main>
      <Schema data={jsonLd} />
      <BlogSingle
        title={title}
        content={content}
        categories={categories?.map((e: SanityConceptItem) => e.pageReference)}
        bannerSidebar={bannerSidebar}
        fullWidthImage={fullWidthImage}
        author={author}
        createdAt={createdAt}
        productCodes={productCodes}
        relatedProducts={relatedProducts}
        relatedPosts={relatedPosts}
      />
    </main>
  )
}
